import ResourceFactory from "./resource-factory";
import Timeline from "./timeline";
import {Registry, TextScript} from "shared"
import Emitter from "../../textScript/emitter";
import CommandFactory from "../../textScript/command-factory";
import TrainingService from "../service";
import {ActionTypeEnum, ResourceTypeEnum, updateWorkspaceFeatures} from "./training-literals";
import ActionStateFactory from "./action-state-factory";

wv.mediaCounter = 0;
wv.trainingAuthoringEnabled = true;

export default class Training {
    name;
    startTime;
    resources;
    states;
    timelines;
    chapters;
    totalSizeHint;
    registry;
    textScript;
    stateResources;
    actionStates;

    constructor({ name, _id }, { resources, states, timelines, chapters, loadingStateWithStepMenu }) {
        const resourceEntries = resourceMap();
        const stateEntries = states.map(state => stateMap(this, state));
        const timelineEntries = timelines.map(timelineMap);
        this.chapterEntries = chapters.map(chapterMap);
        mediaScreenPositioningController.clearMediaScreenData();

        this.name = name;
        this.id = _id.toString();
        this.resources = Object.fromEntries(resourceEntries);
        this.states = Object.fromEntries(stateEntries);
        this.timelines = Object.fromEntries(timelineEntries);
        this.actionStates = [];
        this.chapters = Object.fromEntries(this.chapterEntries);
        this.totalSizeHint = 0;
        this.registry = new Registry();
        this.addRegistryDefaultValues();
        this.textScript = new TextScript(this.registry);
        this.textScript.commandFactory = new CommandFactory(this.registry);
        this.textScript.emitter = new Emitter(this.textScript.commandFactory);
        this.stateResources = [];
        this.stateActions = [];
        this.loadingStateWithStepMenu = loadingStateWithStepMenu;

        this.stepMenuFrame = {
            VRMode: "scene-follow",
            Scene: {
                "position": [-1, 0.5, -2],
                "quaternion": [0, 0, 0, 1],
                "scale": 1
            }
        };

        function resourceMap() {
            const resourceEntries = [];
            for (const resource of resources) {
                if (resource.instances) {
                    const keys = Object.values(resource.instances);
                    for (const key of keys) {
                        resourceEntries.push([key, ResourceFactory.create({id: key, instanceId: key, ...resource})]);
                    }
                } else {
                    resourceEntries.push([resource.id, ResourceFactory.create({id: resource.id, ...resource})]);
                }
            }

            return resourceEntries;
        }

        function stateMap(training, {id, resourceId, resourceStates, actionStates}) {
            const resourceStateEntries = resourceStates.map(rs => {
                const key = rs.instanceId ? rs.instanceId : rs.id;
                return [key, rs];
            });

            const resourceSnapshots = Object.fromEntries(resourceStateEntries);

            if (actionStates && actionStates.length > 0) {
                resourceSnapshots.__actionStates = {};
                actionStates.forEach(as => {
                    const key = as.instanceId ? as.instanceId : as.id;
                    resourceSnapshots.__actionStates[key] = as;
                });
            }

            let identifier = resourceId;
            if (!identifier) {
                identifier = id;
            }
            return [identifier, resourceSnapshots];
        }

        function timelineMap(timeline) {
            return [timeline.id, new Timeline(timeline)];
        }

        function chapterMap(chapter) {
            return [chapter.id, chapter];
        }
    }

    populateResources() {
        const timelineMap = new Map();
        const resources = [];
        const resourceMap = new Map();

        let priorityValue = 1000;
        const processTimeline = (id, timeline) => {
            timelineMap.set(id, 1);
            const timelinesToProcess = [];
            timeline.actionSequence.forEach(action => {
                if (action.type === ActionTypeEnum.COMMAND_ACTION) {
                    if (action.hasOwnProperty('commandAction') && action.commandAction.hasOwnProperty('timelines')) {
                        action.commandAction.timelines.forEach(t_id => {
                            timelinesToProcess.push({
                                id: t_id,
                                timeline: this.timelines[t_id]
                            });
                        });
                    }
                } else if (action.type === ActionTypeEnum.SELECT_ONE_OPTION) {
                    if (action.hasOwnProperty('timelineMap')) {
                        action.timelineMap.forEach(data => {
                            if (data.hasOwnProperty('timelines')) {
                                data.timelines.forEach(t_id => {
                                    timelinesToProcess.push({
                                        id: t_id,
                                        timeline: this.timelines[t_id]
                                    });
                                });
                            }
                        });
                    }

                    if (action.hasOwnProperty('cancelAction') && action.cancelAction.hasOwnProperty('timelines')) {
                        action.cancelAction.timelines.forEach(t_id => {
                            timelinesToProcess.push({
                                id: t_id,
                                timeline: this.timelines[t_id]
                            });
                        });
                    }
                } else if (action.type === 'LoadState') {
                    action.states.forEach(s_id => {
                        if (Object.keys(this.states[s_id]).length > 0) {
                            for (const [key, value] of Object.entries(this.states[s_id])) {
                                if (!resourceMap.has(key) && this.resources.hasOwnProperty(key)) {
                                    resources.push({...this.resources[key], ...{priority: priorityValue}});
                                    resourceMap.set(key, 1);
                                }
                            }
                        }
                    });
                } else if (action.type === ActionTypeEnum.LOAD_TIMELINE) {
                    action.timelines.forEach(t_id => {
                        timelinesToProcess.push({
                            id: t_id,
                            timeline: this.timelines[t_id]
                        });
                    });
                    if (action.hasOwnProperty('prune') && action.prune.hasOwnProperty('timelines')) {
                        action.prune.timelines.forEach(t_id => {
                            timelinesToProcess.push({
                                id: t_id,
                                timeline: this.timelines[t_id]
                            });
                        });
                    }
                }
                priorityValue--;
            });

            timelinesToProcess.forEach(timelineObj => {
                if (!timelineMap.has(timelineObj.id) && this.timelines.hasOwnProperty(timelineObj.id)) {
                    processTimeline(timelineObj.id, timelineObj.timeline);
                }
            });
        };

        const timelinesToProcess = [];
        this.chapterEntries.forEach(chapterArray => {
            const data = chapterArray[1];
            data.entryTimelines.forEach(t_id => {
                if (!timelinesToProcess.find(t => t === t_id)) {
                    timelinesToProcess.push(t_id);
                }
            });

            if (data.hasOwnProperty('prune') && data.prune.hasOwnProperty('timelines')) {
                data.prune.timelines.forEach(t_id => {
                    if (!timelinesToProcess.find(t => t === t_id)) {
                        timelinesToProcess.push(t_id);
                    }
                });
            }

            if (data.hasOwnProperty('restrictionAction')) {
                if (data.restrictionAction.hasOwnProperty('prune') && data.restrictionAction.prune.hasOwnProperty('timelines')) {
                    data.restrictionAction.prune.timelines.forEach(t_id => {
                        if (!timelinesToProcess.find(t => t === t_id)) {
                            timelinesToProcess.push(t_id);
                        }
                    });
                }

                if (data.restrictionAction.hasOwnProperty('timelines')) {
                    data.restrictionAction.timelines.forEach(t_id => {
                        if (!timelinesToProcess.find(t => t === t_id)) {
                            timelinesToProcess.push(t_id);
                        }
                    });
                }
            }
        });

        timelinesToProcess.forEach(t_id => {
            if (!timelineMap.has(t_id) && this.timelines.hasOwnProperty(t_id)) {
                processTimeline(t_id, this.timelines[t_id]);
            }
        });

        let p = resources;
    }

    async loadActionStates() {
        if (!this.actionStates || typeof this.actionStates !== 'object') {
            return;
        }

        for (const state of Object.values(this.actionStates)) {
            if (!state?.type) {
                console.warn("Action state missing type:", state);
                continue;
            }

            try {
                await ActionStateFactory.extract(state);
            } catch (err) {
                console.error(`Failed to extract action state of type '${state.type}':`, err);
            }
        }


    }

    async loadResources(progressBar = true, isPreview = false) {
        model_load_helper.model_appended = true;
        if (wv.effectController.stoppedTrainingId === this.id) {
            return;
        }
        //todo incomplete resource population mechanism
        //this.populateResources();
        wv.isTrainingResourceLoading = true;
        wv.allVolumesForFaces.visible = false;
        const resources = Object.values(this.resources).filter(resource => resource.type !== ResourceTypeEnum.BOUNDARY);

        if (progressBar || isPreview) {
            await this.calculateTotalSizeHint();
            this.initResourceLoadProgressBar(isPreview);
        }
        const loadMediaPromises = [];
        wv.mediaCounter = 0;
        const loadResourcePromises = resources.map(resource => resource.load(progressBar ? this.onResourceLoad.bind(this) : () => {
        }, loadMediaPromises));
        await Promise.all(loadResourcePromises);

        while (wv.mediaCounter !== loadMediaPromises.length) {
            if (wv.effectController.stoppedTrainingId === this.id) {
                break;
            }
            await sleep(200);
        }
        this.resourceLoadCompleted(isPreview);
        if (!(wv.effectController.trainingMeeting || isServerLessTrainingUIMeeting())) {
            wv.allVolumesForFaces.visible = true;
        }

    }

    prepareResources(resourceStates = {}) {
        const keys = Object.keys(resourceStates).filter(key => key !== '__actionStates');
        this.stateResources = keys;

        for (let id of keys) {
            const snapshot = resourceStates[id];
            const resource = this.resources[id];
            if (resource) {
                resource.prepareState(snapshot);
            }
        }

        this.stateActions = resourceStates.__actionStates ? Object.keys(resourceStates.__actionStates) : [];
        this.processActionStates(resourceStates.__actionStates);

    }

    processActionStates(actionStates) {
        if (!actionStates) {
            actionStates = [];
        }

        const stepMenuFrames = Object.values(actionStates).filter(state => state.type === 'Step menu frame');
        if (stepMenuFrames.length > 0) {

            const stepMenuFrame = stepMenuFrames[0];
            this.stepMenuFrame = {
                ...this.stepMenuFrame,
                ...stepMenuFrame
            };
        }
    }

    prepareStates(states) {
        for (let state of states)
            if (typeof state === "string") {
                this.prepareResources(this.states[state])
            } else {
                const resourceStateEntries = state.resourceStates.map(resource => [resource.id, resource]);

                this.prepareResources(Object.fromEntries(resourceStateEntries));
            }
    }

    finalizeLoadState(options) {
        // If loadViewpoint is false, camera state was already loaded in the initial loop
        if (!options.loadViewpoint) {
            return;
        }

        const shouldApplyViewpoint = !((isVRModeEnabled() || isMobileARModeEnabled()) && options.viewpointApplyMode === '2D');
        const cameraOptions = {
            ...options,
            loadViewpoint: shouldApplyViewpoint
        };

        this.resources['camera'].loadState(cameraOptions);
    }

    applyStepMenuFrameToActiveMenu() {
        if (InstructionManager && InstructionManager.instructionMenuController) {
            InstructionManager.instructionMenuController.applyStepMenuFrameToMenu(this.stepMenuFrame);
        }
    }

    async loadStates(options) {
        // When "Keep Existing Scene" is OFF (replace mode), we need to reset ALL models to default state
        // When "Keep Existing Scene" is ON (patch/append mode), only apply state to resources in the saved state
        const isReplaceMode = !options.patchState && !options.appendState && !options.staticPatchState;

        if (isReplaceMode) {
            for (const child of CADModel.modelArray) {
                resetModelPositionAndRotation(child.modelId);
                await resetAllVolumePosRotOfThisModel(child.modelId);
            }
            
            if (point_cloud_helper && point_cloud_helper.point_cloud) {
                for (const child of point_cloud_helper.point_cloud) {
                    resetModelPositionAndRotation(child.modelId);
                    await resetAllVolumePosRotOfThisModel(child.modelId);
                }
            }
            
            moved_branch_matrices = {};
            moved_volume_matrices = {};
        }
        
        const resources = Object.entries(this.resources)
            .filter(([key, resource]) => {
                if (isReplaceMode) {
                    return this.stateResources.includes(key) || resource.type !== ResourceTypeEnum.BOUNDARY;
                } else {
                    return this.stateResources.includes(key);
                }
            })
            .map(([, resource]) => resource);

        const actionStates = this.actionStates;

        let boundaryExists;
        for (const resource of resources) {
            if (resource.type === ResourceTypeEnum.BOUNDARY) {
                boundaryExists = true;
                break;
            }
            if (resource.type === ResourceTypeEnum.CAMERA) {
                resource.ignoreLockMovementOrientation = options.ignoreLockMovementOrientation === true;
            }
        }

        if (!boundaryExists) {
            BoundaryService.setConfigurations([]);
        }

        resources
            .filter(resource => options.loadViewpoint ? resource.id !== 'camera' : true) /// if the 'loadViewpoint' is enabled, then 'camera' state loads at the end.
            .map(resource => resource.loadState(options));

        this.applyStepMenuFrameToActiveMenu();
        this.finalizeLoadState(options);
    }

    initResourceLoadProgressBar(isPreview = false) {
        if (wv.effectController.stoppedTrainingId === this.id) {
            return;
        }

        if (!isPreview) {
            wv.trainingSplashScreen?.dispose();
            const splashScreen = new TrainingSplashScreen();
            splashScreen.create();
            splashScreen.setProgress(0);
            wv.trainingSplashScreen = splashScreen;
            modelLoadingProgressBar.setForceInvisibility(true);
        } else {
            modelLoadingProgressBar.setText("Loading...");
            modelLoadingProgressBar.show(true);
        }
    }

    resourceLoadCompleted(isPreview = false) {
        if (isPreview) {
            modelLoadingProgressBar.show(false);
        }

        if (wv.trainingSplashScreen) {
            wv.trainingSplashScreen.setProgress(100);

            setTimeout(() => {
                wv.trainingSplashScreen?.dispose();
                wv.isTrainingResourceLoading = false;
            }, 2000);
        } else {
            wv.isTrainingResourceLoading = false;
        }
        modelLoadingProgressBar.setForceInvisibility(false);
    }

    incrementTotalSizeHint(value) {
        this.totalSizeHint += value;
    }

    async calculateTotalSizeHint() {
        this.totalSizeHint = 0;
        for (let resource in this.resources) {
            let sizeHint = await this.resources[resource].getSizeHint();
            this.incrementTotalSizeHint(sizeHint);
        }

        return this.totalSizeHint;
    }

    getTotalSizeHint() {
        return this.totalSizeHint;
    }

    onResourceLoad(deltaLoaded) {
        const newProgress = wv.trainingSplashScreen?.getCurrentProgress() + (deltaLoaded * 100) / this.getTotalSizeHint();
        wv.trainingSplashScreen?.setProgress(newProgress);
    }

    addRegistryDefaultValues() {
        const pairs = {
            username: wv.name,
            trainingName: this.name
        };

        for (let key in pairs) {
            this.registry.set(key, pairs[key]);
        }
    }

    setStartTime(time) {
        this.startTime = time;
    }

    async end(retainState = false) {
        if (!retainState) {
            await this.loadStates([]);
        } else {
            updateWorkspaceFeatures();
        }

        const evaluationId = wv.effectController.meetingInfo.trainingEvaluationId;
        wv.effectController.meetingInfo.trainingEvaluationId = null;
        const completionTime = Date.now() - this.startTime;
        await this.updateEvaluation(evaluationId, 'end', {completionTime});
        wv.effectController.serverlessTrainingRunning = false;
    }

    async updateEvaluation(evaluationId, state = "running", addedInfo = {}) {
        evaluationId = evaluationId || wv.effectController.meetingInfo.trainingEvaluationId;
        let report = this.registry.getReport();
        if (!window.noserver && evaluationId) {
            // don't await because of performance issues
            TrainingService.updateEvaluationReport(
                evaluationId,
                {report}
            );
        }

        report = {...report, ...addedInfo};
        window.parent.postMessage({
            messageType: "TrainingReport",
            state: state,
            report: report,
            trainingId: this.id,
            meetingId: wv.effectController.meetingInfo?.data?.id,
        }, "*");
    }
}
